import { AbstractAdminEntityService } from "./AbstractAdminEntityService";
import { AdminSession, AdminUser, AuthResult, ListAdminUsersResponse, PermissionCategory } from "../http/protocol";
import { HttpService } from "../http/HttpService";

export type PermissionCategoryType = Extract<
    PermissionCategory,
    PermissionCategory.BUDGET_SMALL | PermissionCategory.BUDGET_MEDIUM | PermissionCategory.BUDGET_LARGE
>;

export interface PermissionLimitType {
    permissionCategory: PermissionCategoryType;
    dailyLimit?: number;
    largeAmount?: number;
}

export interface DailyBudgetType {
    dailySpentAmount?: number;
    permissionLimit?: PermissionLimitType;
}

export class AdminUserService extends AbstractAdminEntityService<AdminUser, ListAdminUsersResponse> {

    constructor(http: HttpService) {
        super("/admin-user", http);
    }

    getSelf(): Promise<AdminUser> {
        return this.http.get(this.entityPath + "/self");
    }

    resetPassword(userId?: number): Promise<AdminUser> {
        return this.http.get(this.entityPath + "/reset-password/" + userId);
    }

    invalidateSessions(userId: number): Promise<void> {
        return this.http.get(this.entityPath + `/sessions/invalidate/${userId}`);
    }

    getSessions(userId: number): Promise<AdminSession[]> {
        return this.http.get(this.entityPath + `/sessions/${userId}`);
    }

    initiateAppMfa(): Promise<AuthResult> {
        return this.http.get(this.entityPath + "/mfa/init");
    }

    verifyAppMfa(code: string): Promise<AuthResult> {
        return this.http.post(this.entityPath + "/mfa/verify/",
            { provider: "AUTHENTICATOR_APP", verificationCode: code });
    }

    // if users want to reset mfa, they need to provide their current code
    resetAppMfa(code: string): Promise<AuthResult> {
        return this.http.post(this.entityPath + "/mfa/reset",
            { provider: null, verificationCode: code });
    }

    setMfaProvider(provider: string): Promise<AuthResult> {
        return this.http.post(this.entityPath + "/mfa/method",
            { provider: provider, verificationCode: null });
    }

    // used by super admin on behalf of other admins
    resetMfa(userId: number) {
        return this.http.delete(this.entityPath + `/${userId}/mfa`);
    }

    getDailyBudget(userId: number): Promise<DailyBudgetType> {
        return this.http.get(this.entityPath + `/daily-budget/${userId}`);
    }

    clearDailySpentAmount(userId: number): Promise<DailyBudgetType> {
        return this.http.post(this.entityPath + `/clear-daily-budget/${userId}`, {});
    }
}
